import { FC, useEffect, useState } from 'react';
import { Modal } from '../components/Modal';
import { IonCardContent, IonContent, IonLabel, IonSegmentButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { ICaremapNote } from '../notes/model/NotesClient';
import { createUseStyles } from 'react-jss';
import { EnvelopeOutlineIcon } from '../theme/Icons';
import { Reaction } from './reactions/Reaction';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { IOSTabs } from '../components/IOSTabs';

const useStyles = createUseStyles({
    content: {
        paddingTop: 0
    },

    row: {
        gap: 8,
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center'
    },

    segmentWrapper: {
        marginBottom: 18
    },

    information: {
        paddingLeft: 8,
        paddingRight: 8
    }
});

export type IPostInteractionInfoType = 'notifications' | 'reactions';
export type IPostInteractionModalProps = {
    closeHandler: () => void;
    modalInfo?: {
        post: ICaremapNote;
        type: IPostInteractionInfoType;
    }
};

export const PostInteractionModal: FC<IPostInteractionModalProps> = ({ modalInfo, closeHandler = () => {} }) => {
    const classes = useStyles();

    const [infoType, setInfoType] = useState<IPostInteractionInfoType>(modalInfo?.type || 'reactions');

    const mixpanel = useInstance(MixPanelEventEmitter);
    const reactionsModel = useInstance(CaremapNotesModelFactory).forActiveCaremap().reactions;
    const possibleReactions = reactionsModel.getSupportedReactions();

    useEffect(() => {
        if (modalInfo) {
            setInfoType(modalInfo.type);
        }
    }, [modalInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (modalInfo) {
            if (modalInfo.type === 'reactions') {
                let count = 0;
                possibleReactions.forEach(reaction => {
                    count += reactionsModel.getNames({ note: modalInfo.post, reaction }).length;
                });

                mixpanel.track('Reactions Modal Opened', () => ({
                    '# of Reactions': count
                }));
            } else {
                mixpanel.track('Sent To Modal Opened', () => ({
                    '# of Notifications Sent': reactionsModel.getNames({ note: modalInfo.post, reaction: 'notification-sent:email' }).length
                }));
            }
        }
    }, [modalInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleType = (type: IPostInteractionInfoType) => {
        if (infoType !== 'reactions' && type === 'reactions') {
            mixpanel.track('Toggled Reactions / Sent Modal', () => ({
                from: 'Sent To',
                to: 'Reactions'
            }));
        } else {
            mixpanel.track('Toggled Reactions / Sent Modal', () => ({
                from: 'Reactions',
                to: 'Sent To'
            }));
        }
        setInfoType(type);
    };

    return (
        <Modal
            desktopMaxWidth={500}
            desktopMaxHeight={400}
            data-id="post-info-modal"
            closeHandler={closeHandler}
            isOpen={Boolean(modalInfo)}
            title="Post Activity"
        >
            <IonContent>
                <IonCardContent className={classes.content}>
                    <div className={classes.segmentWrapper}>
                        {/* if user doesn't have access to Sent To, hide toggle */}
                        {modalInfo && reactionsModel.getNames({note: modalInfo.post, reaction: 'notification-sent:email'}).length ? (
                            <IOSTabs data-id="post-info-toggle" value={infoType}>
                                <IonSegmentButton value="reactions" onClick={() => toggleType('reactions')}>
                                    <IonLabel>Reactions</IonLabel>
                                </IonSegmentButton>
                                <IonSegmentButton value="notifications" onClick={() => toggleType('notifications')}>
                                    <IonLabel>Sent To</IonLabel>
                                </IonSegmentButton>
                            </IOSTabs>
                        ) : null}
                    </div>
                    <div className={classes.information}>
                        {modalInfo && infoType === 'reactions' ? (
                            <div data-id="reactions">
                                {possibleReactions.map(reaction => (
                                    reactionsModel.getNames({ note: modalInfo.post, reaction }).map((name, idx) => (
                                        <div key={idx} className={classes.row}>
                                            <Reaction reaction={reaction} hasReaction />
                                            <div>{name}</div>
                                        </div>
                                    ))
                                ))}
                            </div>
                        ) : (
                            modalInfo && (
                                <div data-id="notifications">
                                    {reactionsModel.getNames({ note: modalInfo.post, reaction: 'notification-sent:email' }).map((name, idx) => {
                                        const names = name.split(' ');
                                        const fullName = names.length > 2 ? `${names[0]} ${names[1]}` : names[0];
                                        const email = names.length > 2 ? names[2] : names[1];
                                        return (
                                            <div key={idx} className={classes.row} style={{alignItems: 'flex-start'}}>
                                                <div><EnvelopeOutlineIcon /></div>
                                                <div>
                                                    <div>{fullName.replaceAll('"', '')}</div>
                                                    <div>{email.replaceAll('<', '').replaceAll('>', '')}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        )}
                    </div>
                </IonCardContent>
            </IonContent>
        </Modal>
    );
};
